import { html, nothing } from 'lit';
import { classMap } from 'lit/directives/class-map.js';
import { ifDefined } from 'lit/directives/if-defined';
import componentStyles from './main-nav.css.lit.ts';
import QueryComponent from '../../base/query-component/QueryComponent.js';
import {isExternalUrl, leavingSiteAlertHandler, externalLinkHoverText} from "../../utils/link";

export default class MainNav extends QueryComponent {
  static styles = [super.styles, componentStyles];
  static get properties() {
    return {
      activeItem: { type: Number },
      globalLinks: { type: Array },
      links: { type: Array },
      mobileToggle: { type: Object },
      title: { type: String },
      search: { type: Object },
      secondaryLinks: { type: Array },
      topAdjustment: { type: Number },
      isMLP: { type: Boolean },
      isHPSite: { type: Boolean },
    };
  }

  constructor() {
    super();
    this.activeItem = null;
    this.globalLinks = [];
    this.secondaryLinks = [];
    this.mobileToggle = {};
    this.topAdjustment = 48;
    this.isMLP = false;
    this.isHPSite = false;
  }

  getHeight(element) {
    return document.querySelector(element)
      ? document.querySelector(element).getBoundingClientRect().height
      : 0;
  }

  updateNavTop() {
    const particleAlertBannerBlockHeight = this.getHeight('#block-particle-alertbanner');
    //const alertBlockHeight = this.getHeight('#block-particle-alertbanner');
    const toolbarHeight = this.getHeight('#toolbar-bar');
    const toolbarTrayHeight = this.getHeight(
      '#toolbar-item-administration-tray'
    );
    const envIndicatorHeight = this.getHeight('#environment-indicator');
    const headerNavHeight = this.getHeight('hf-header .order-last');
    let alertBlockHeight = particleAlertBannerBlockHeight;
    // Shimming the calculated height of the alertBlock to bump up height
    // to 70 if calculated at less than that.
    if (particleAlertBannerBlockHeight < 70) {
      alertBlockHeight = 70
    }
    this.topAdjustment =
      alertBlockHeight +
      toolbarHeight +
      envIndicatorHeight +
      headerNavHeight +
      toolbarTrayHeight;
  }

  firstUpdated() {
    this.mobileToggle = this.links.length ? this.links[0] : {};
    this.mobileToggle.activeIcon = this.mobileToggle.iconname;
    this.updateNavTop();
    window.addEventListener('load', () => {
      this.updateNavTop();
    });
    new ResizeObserver(() => {
      this.updateNavTop();
    }).observe(document.querySelector('body'));
  }

  toggleFlyout(e) {
    window.dispatchEvent(new CustomEvent('hf-search-hero-close-panel'));
    const target = parseInt(e.currentTarget.getAttribute('data-index'), 10);
    if (target === 0 || target === -1) {
      e.preventDefault()
      this.activeItem = target === this.activeItem ? null : target;
      // Add class to the menu container
      const main_nav = this.shadowRoot.querySelector('.main-navigation');
      main_nav.classList.toggle("active");
      // Disable Scroll while menus are open
      document.body.style.overflow = this.activeItem !== null ? 'hidden' : 'auto';
    }
    this.mobileToggle.activeIcon =
      target === -1 && this.activeItem === -1
        ? 'close'
        : this.mobileToggle.iconname;
    if (isExternalUrl(e.currentTarget.getAttribute('href')) && !leavingSiteAlertHandler()) {
      e.preventDefault()
    }
    this.requestUpdate('activeItem', this.activeItem);
  }

  renderNavFlyout(itemIcon, itemText, link, index) {
    // Enable Scroll
    document.body.style.overflow = 'auto';

    if (this.isHPSite) {
      return html`
        <a
          href=${link.url}
          @click="${this.toggleFlyout}"
          title="${ifDefined(isExternalUrl(link.url) ? externalLinkHoverText() : undefined)}"
          data-index="${index}"
          class="${classMap({
            'main-navigation--link': true,
            [`link--${index}`]: true,
          })}"
        >
          <hf-icon
            name="${itemIcon}"
            class="main-navigation--icon"
            height="24"
            width="24"
          ></hf-icon>
          <span class="main-navigation--text"
            >${itemText}</span
          >
        </a>
        ${index === 0 ? html`
          <hf-hp-nav-flyout
            ?isOpen="${this.activeItem === index}"
            .links="${link.sub}"
            .search="${this.search}"
          ></hf-hp-nav-flyout>
        ` : nothing}
      `;
    } else if(this.links.length) {
      return html`
        <a
          href=${link.url}
          title="${ifDefined(isExternalUrl(link.url) ? externalLinkHoverText() : undefined)}"
          @click="${this.toggleFlyout}"
          data-index="${index}"
          class="${classMap({
            'main-navigation--link': true,
            [`link--${index}`]: true,
          })}"
        >
          <hf-icon
            name="${itemIcon}"
            class="main-navigation--icon"
            height="24"
            width="24"
          ></hf-icon>
          <span class="main-navigation--text"
            >${itemText}</span
          >
        </a>
        ${index === 0 ? html`
        <hf-nav-flyout
          ?isOpen="${this.activeItem === index}"
          .links="${link.sub}"
          .search="${this.search}"
        ></hf-nav-flyout>` : nothing}
      `;
    } else {
      return html`
        <a
          href=${link.url}
          title="${ifDefined(isExternalUrl(link.url) ? externalLinkHoverText() : undefined)}"
          target="_self"
          class="main-navigation--link"
        >
          <hf-icon
            name="${link.iconname}"
            class="main-navigation--icon"
            height="24"
            width="24"
          ></hf-icon>
          <span class="main-navigation--text"
            >${link.text}</span
          >
        </a>
      `;
    }
  }

  render() {
    return html`
      <style>
        :host {
          --topAlign: ${this.topAdjustment}px;
        }
      </style>
      <nav
        role="navigation"
        aria-label="${this.title}"
        class="${classMap({
          'main-navigation': true,
          'main-navigation--mobile': this.isMobile,
          'main-navigation--mlp': this.isMLP,
          'main-navigation--hp': this.isHPSite,
        })}"
      >
        ${this.isMobile
          ? html`
              ${this.activeItem !== -1
                ? html`
                    <hf-button
                      ariaLabel="${ifDefined(this.mobileToggle.iconname)}"
                      icon="${ifDefined(this.mobileToggle.iconname)}"
                      @click="${this.toggleFlyout}"
                      data-index="-1"
                      type="menu"
                    >
                    </hf-button>
                  `
                : html`
                    <hf-button
                      ariaLabel="${Drupal.t('close')}"
                      icon="close"
                      @click="${this.toggleFlyout}"
                      data-index="-1"
                      type="menu-close"
                    >
                    </hf-button>
                  `}
            `
          : html`
              <ul class="main-navigation--list">
                ${this.links.map((link, index) => {
                  const itemIcon =
                    this.activeItem === index ? 'close' : link.iconname;
                  const itemText =
                    this.activeItem === index
                      ? `close ${link.text}`
                      : link.text;
                  return html`<li
                    class="${classMap({
                      'main-navigation--item': true,
                      'main-navigation--item--open': this.activeItem === index,
                    })}"
                  >
                    ${this.renderNavFlyout(itemIcon, itemText, link, index)}
                  </li>`;
                })}
              </ul>
          `}
        ${this.isHPSite
          ? html`
            <hf-hp-nav-flyout-mobile
              ?isOpen="${this.activeItem === -1}"
              .links="${this.links}"
              .globalLinks="${this.globalLinks}"
              .search="${this.search}"
              .secondaryLinks="${this.secondaryLinks}"
              class="nav-flyout-mobile"
            ></hf-hp-nav-flyout-mobile>
            `
          : html`
            <hf-nav-flyout-mobile
              ?isOpen="${this.activeItem === -1}"
              .links="${this.links}"
              .globalLinks="${this.globalLinks}"
              .search="${this.search}"
              .secondaryLinks="${this.secondaryLinks}"
              class="nav-flyout-mobile"
            ></hf-nav-flyout-mobile>
          `}
      </nav>
    `;
  }
}

customElements.define('hf-main-nav', MainNav);
